import { createTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

const createCustomTheme = (mode) => ({
  palette: {
    mode,
    ...(mode === "light"
      ? {
          // Light mode colors - refined professional palette
          primary: {
            main: "#2563eb", // Modern professional blue
            light: "#3b82f6",
            dark: "#1d4ed8",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#0ea5e9", // Complementary bright blue
            light: "#38bdf8",
            dark: "#0284c7",
            contrastText: "#ffffff",
          },
          success: {
            main: "#4caf50",
            light: "#80e27e",
            dark: "#087f23",
          },
          info: {
            main: "#2196f3",
            light: "#6ec6ff",
            dark: "#0069c0",
          },
          warning: {
            main: "#ff9800",
            light: "#ffc947",
            dark: "#c66900",
          },
          error: {
            main: "#f44336",
            light: "#ff7961",
            dark: "#ba000d",
          },
          background: {
            default: "#f5f5f7", // Subtle light background
            paper: "#ffffff",
            alternate: "#f8fafc",
          },
          text: {
            primary: "#0f172a", // Deep slate for better readability
            secondary: "#475569", // Slate gray for subtlety
            disabled: "rgba(71, 85, 105, 0.38)",
          },
          divider: "rgba(0, 0, 0, 0.08)",
        }
      : {
          // Dark mode colors - modern professional palette
          primary: {
            main: "#3b82f6", // Brighter blue for dark mode visibility
            light: "#60a5fa",
            dark: "#2563eb",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#38bdf8", // Bright sky blue
            light: "#7dd3fc",
            dark: "#0284c7",
            contrastText: "#ffffff",
          },
          success: {
            main: "#66bb6a",
            light: "#98ee99",
            dark: "#338a3e",
          },
          info: {
            main: "#42a5f5",
            light: "#80d6ff",
            dark: "#0077c2",
          },
          warning: {
            main: "#ffa726",
            light: "#ffd95b",
            dark: "#c77800",
          },
          error: {
            main: "#ef5350",
            light: "#ff867c",
            dark: "#b61827",
          },
          background: {
            default: "#121212",
            paper: "#1e1e1e",
            alternate: "#1f1f23",
          },
          text: {
            primary: "#f1f5f9",
            secondary: "#cbd5e1",
            disabled: "rgba(203, 213, 225, 0.5)",
          },
          divider: "rgba(255, 255, 255, 0.06)",
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      letterSpacing: "-0.01562em",
      lineHeight: 1.2,
      color: mode === "light" ? "#172b4d" : "#e0e0e0",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 600,
      letterSpacing: "-0.00833em",
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 600,
      letterSpacing: "0em",
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      letterSpacing: "0.00735em",
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      letterSpacing: "0em",
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1.125rem",
      fontWeight: 600,
      letterSpacing: "0.0075em",
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 500,
      letterSpacing: "0.00938em",
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 500,
      letterSpacing: "0.00714em",
      lineHeight: 1.57,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      letterSpacing: "0.00938em",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      letterSpacing: "0.01071em",
      lineHeight: 1.43,
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "0.02857em",
      lineHeight: 1.75,
      textTransform: "none",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      letterSpacing: "0.03333em",
      lineHeight: 1.66,
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 400,
      letterSpacing: "0.08333em",
      lineHeight: 2.66,
      textTransform: "uppercase",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@global": {
          "@import": "url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap')",
        },
        html: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          boxSizing: "border-box",
          scrollBehavior: "smooth",
        },
        body: {
          backgroundColor: mode === "light" ? "#f5f7fa" : "#121212",
          color: mode === "light" ? "#172b4d" : "#e0e0e0",
          transition: "background-color 0.2s ease-in-out",
        },
        "*, *::before, *::after": {
          boxSizing: "inherit",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === "light" ? "#ffffff" : "#1c1c1c",
          color: mode === "light" ? "#172b4d" : "#e0e0e0",
          boxShadow: mode === "light" 
            ? "0px 2px 4px -1px rgba(0,0,0,0.05), 0px 4px 5px 0px rgba(0,0,0,0.03), 0px 1px 10px 0px rgba(0,0,0,0.02)"
            : "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
          backgroundImage: "none",
        },
        colorDefault: {
          backgroundColor: mode === "light" ? "#ffffff" : "#1c1c1c",
          color: mode === "light" ? "#172b4d" : "#e0e0e0",
        },
        colorPrimary: {
          backgroundColor: mode === "light" ? alpha("#3f51b5", 0.95) : alpha("#1c1c1c", 0.95),
          color: mode === "light" ? "#ffffff" : "#e0e0e0",
          backdropFilter: "blur(8px)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          width: 280,
        },
        paper: {
          width: 280,
          backgroundColor: mode === "light" ? "#ffffff" : "#1c1c1c",
          color: mode === "light" ? "#172b4d" : "#e0e0e0",
          borderRight: `1px solid ${mode === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.06)"}`,
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",
          padding: "8px 16px",
        },
        sizeSmall: {
          padding: "6px 12px",
          fontSize: "0.8125rem",
        },  
        sizeLarge: {
          padding: "10px 20px",
          fontSize: "1rem",
        },
        contained: {
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.08)",
          "&:hover": {
            boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.1)",
          },
        },
        containedPrimary: {
          backgroundColor: mode === "light" ? "#3f51b5" : "#5c6bc0",
          "&:hover": {
            backgroundColor: mode === "light" ? "#303f9f" : "#3949ab",
          },
        },
        containedSecondary: {
          backgroundColor: mode === "light" ? "#f50057" : "#ff4081",
          "&:hover": {
            backgroundColor: mode === "light" ? "#c51162" : "#e91e63",
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            backgroundColor: mode === "light" ? alpha("#3f51b5", 0.04) : alpha("#5c6bc0", 0.08),
          },
        },
        outlinedPrimary: {
          borderColor: mode === "light" ? "#3f51b5" : "#5c6bc0",
          "&:hover": {
            backgroundColor: mode === "light" ? alpha("#3f51b5", 0.04) : alpha("#5c6bc0", 0.08),
            borderColor: mode === "light" ? "#303f9f" : "#3949ab",
          },
        },
        text: {
          padding: "8px 12px",
          "&:hover": {
            backgroundColor: mode === "light" ? alpha("#3f51b5", 0.04) : alpha("#5c6bc0", 0.08),
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 10,
          borderRadius: 8,
          color: mode === "light" ? "#5e6c84" : "#a0a0a0",
          "&:hover": {
            backgroundColor: mode === "light" ? alpha("#3f51b5", 0.04) : alpha("#5c6bc0", 0.08),
          },
        },
        colorPrimary: {
          color: mode === "light" ? "#3f51b5" : "#5c6bc0",
          "&:hover": {
            backgroundColor: mode === "light" ? alpha("#3f51b5", 0.08) : alpha("#5c6bc0", 0.12),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: mode === "light" 
            ? "0px 2px 4px -1px rgba(0,0,0,0.05), 0px 4px 5px 0px rgba(0,0,0,0.03), 0px 1px 10px 0px rgba(0,0,0,0.02)"
            : "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
          transition: "box-shadow 0.3s, transform 0.3s",
          overflow: "visible",
          border: mode === "light" ? "1px solid rgba(0, 0, 0, 0.05)" : "1px solid rgba(255, 255, 255, 0.05)",
          "&:hover": {
            boxShadow: mode === "light" 
              ? "0px 7px 8px -4px rgba(0,0,0,0.08), 0px 12px 17px 2px rgba(0,0,0,0.05), 0px 5px 22px 4px rgba(0,0,0,0.04)"
              : "0px 7px 8px -4px rgba(0,0,0,0.2), 0px 12px 17px 2px rgba(0,0,0,0.14), 0px 5px 22px 4px rgba(0,0,0,0.12)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: mode === "light" 
            ? "0px 2px 4px -1px rgba(0,0,0,0.05), 0px 4px 5px 0px rgba(0,0,0,0.03), 0px 1px 10px 0px rgba(0,0,0,0.02)"
            : "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)", 
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: mode === "light" ? alpha("#3f51b5", 0.05) : alpha("#5c6bc0", 0.15),
          "& .MuiTableCell-root": {
            color: mode === "light" ? "#172b4d" : "#e0e0e0",
            fontWeight: 600,
            fontSize: "0.875rem",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.06)"}`,
          padding: "16px",
        },
        head: {
          fontWeight: 600,
          color: mode === "light" ? "#172b4d" : "#e0e0e0",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          height: 8,
          backgroundColor: mode === "light" ? alpha("#3f51b5", 0.08) : alpha("#5c6bc0", 0.15),
        },
        bar: {
          borderRadius: 10,
        },
        colorPrimary: {
          backgroundColor: mode === "light" ? alpha("#3f51b5", 0.08) : alpha("#5c6bc0", 0.15),
          "& .MuiLinearProgress-bar": {
            backgroundColor: mode === "light" ? "#3f51b5" : "#5c6bc0",
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: mode === "light" ? "#3f51b5" : "#5c6bc0",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.06)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
          fontSize: "0.75rem",
        },
        filled: {
          backgroundColor: mode === "light" ? alpha("#3f51b5", 0.12) : alpha("#5c6bc0", 0.24),
          color: mode === "light" ? "#3f51b5" : "#5c6bc0",
          "&.MuiChip-colorPrimary": {
            backgroundColor: mode === "light" ? alpha("#3f51b5", 0.12) : alpha("#5c6bc0", 0.24),
            color: mode === "light" ? "#3f51b5" : "#5c6bc0",
          },
          "&.MuiChip-colorSecondary": {
            backgroundColor: mode === "light" ? alpha("#f50057", 0.12) : alpha("#ff4081", 0.24),
            color: mode === "light" ? "#f50057" : "#ff4081",
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === "light" ? "#3f51b5" : "#5c6bc0",
          color: "#ffffff",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === "light" ? "rgba(23, 43, 77, 0.95)" : "rgba(224, 224, 224, 0.95)",
          color: mode === "light" ? "#ffffff" : "#121212",
          fontSize: "0.75rem",
          padding: "8px 12px",
          borderRadius: 4,
          boxShadow: mode === "light" 
            ? "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)"
            : "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
        },
        arrow: {
          color: mode === "light" ? "rgba(23, 43, 77, 0.95)" : "rgba(224, 224, 224, 0.95)",
        },
      },
    },
  },
});

export const lightTheme = createTheme(createCustomTheme("light"));
export const darkTheme = createTheme(createCustomTheme("dark"));

